/// <amd-module name="Build/Admin/Pool/Components/PoolTrendCard" />
import { ErrorComponent } from "Analytics/Shared/Analytics-Visuals-Common/ErrorComponent/ErrorComponent";
import { LoadingComponent } from "Analytics/Shared/Analytics-Visuals-Common/LoadingComponent/LoadingComponent";
import { gridLineColor } from "Analytics/Shared/Common/Constants";
import { TrendBy } from "Analytics/Shared/Common/Types";
import { ITrendChartAggregateData, ITrendChartData, ITrendChartReportConfiguration } from "Analytics/Shared/TrendChart/Common/Types";
import { AreaChartView } from "Analytics/Shared/Visuals/AreaChartView/ControllerViews/AreaChartView";
import * as Colors from "Build/Admin/Pool/Colors";
import { ConcurrencySeries } from "Build/Admin/Pool/Components/ConcurrencySeries";
import { QueuedJobsSeries } from "Build/Admin/Pool/Components/QueuedJobsSeries";
import { RunningJobsSeries } from "Build/Admin/Pool/Components/RunningJobsSeries";
import * as Resources from "Build/Admin/Pool/Resources";
import { IPoolAnalyticsTrendCardState, PoolAnalyticsStore } from "Build/Admin/Pool/Stores/PoolAnalyticsStore";
import { PoolDataStrings } from "Build/Admin/Pool/Types";
import * as React from "react";
import { localeFormat } from "VSS/Core/Util/String";
import * as Charts_Contracts from "VSS/Features/Charts/Contracts";
import { ChartProps, DataPoint, TooltipLineItem } from "VSS/Features/Charts/Contracts";
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { Card } from "VSSUI/Card";
import { SpinnerSize } from "VSSUI/Spinner";

export interface IPoolTrendCardProps extends IVssComponentProperties {
    confValues: ITrendChartReportConfiguration;
    errorText?: string;
    isHosted: boolean;
    onDataRendered?: () => void;
    isPublic: boolean;
    store: PoolAnalyticsStore;
}

export class PoolTrendCard extends VssComponent<IPoolTrendCardProps, IPoolAnalyticsTrendCardState> {
    private _onDataRenderedCalled = false;

    constructor(props: IPoolTrendCardProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            errorText: this.props.errorText
        } as Readonly<IPoolAnalyticsTrendCardState>;
    }

    public componentDidMount(): void {
        super.componentDidMount();
        if (this.props) {
            const { isPublic, store } = this.props;

            store.addChangedListener(this._onStoreUpdate);
            this.setState(isPublic ? store.getPublicTrendState() : store.getPrivateTrendState());
            this.performOnDataRendered();
        }
    }

    public componentDidUpdate(prevProps: IPoolTrendCardProps, prevState: IPoolAnalyticsTrendCardState): void {
        super.componentDidUpdate(prevProps, prevState);
        this.performOnDataRendered();
    }

    public componentWillUnmount() {
        if (this.props) {
            this.props.store.removeChangedListener(this._onStoreUpdate);
        }
        super.componentWillUnmount();
    }

    public render() {
        return (
            <Card
                className={this.props.className}
                titleProps={{
                    text: this._getChartTitle()
                }}
            >
                {this.getElementToDisplay()}
            </Card>
        );
    }

    private getElementToDisplay(): JSX.Element {
        if (this.state.errorText) {
            return <ErrorComponent className=" flex-grow" errorMessage={this.state.errorText} />;
        } else if (this.state.confValues != null && this.state.data != null) {
            const chartData = this.state.data;
            return (
                <div className="flex-row flex-wrap flex-grow">
                    <div className="flex-grow">
                        {this._verifyChartData(chartData) && <AreaChartView chartOptions={this._getChartOptions(chartData)} />}
                    </div>
                </div>
            );
        } else {
            return <LoadingComponent className="card-spinner flex-column flex-grow" size={SpinnerSize.large} />;
        }
    }

    private _getChartOptions = (chartData: ITrendChartData): ChartProps => {
        const { isHosted, isPublic } = this.props;
        let xAxisLabelValues: any[] = chartData.primaryChartData[PoolDataStrings.RunningJobs].map((d: ITrendChartAggregateData) => d.date);

        const series: Charts_Contracts.DataSeries[] = [];

        const concurrencyData = chartData.primaryChartData[PoolDataStrings.Concurrency].map((d: ITrendChartAggregateData) => d.aggrValue);
        const concurrencySeries = new ConcurrencySeries(concurrencyData);
        series.push({
            data: concurrencyData,
            color: concurrencySeries.color,
            name: !isHosted && isPublic ? Resources.OnlineAgents : concurrencySeries.name,
            plotDashedStyle: true
        });

        const queuedJobsData = chartData.primaryChartData[PoolDataStrings.QueuedJobs];
        const queuedJobsSeries = new QueuedJobsSeries(queuedJobsData.map((d: ITrendChartAggregateData) => d.aggrValue));
        series.push({
            data: queuedJobsData.map(d => (d.aggrValue !== undefined ? d.aggrValue : null)),
            color: queuedJobsSeries.color,
            name: queuedJobsSeries.name
        });

        const runningJobsData = chartData.primaryChartData[PoolDataStrings.RunningJobs].map((d: ITrendChartAggregateData) => d.aggrValue);
        const runningJobsSeries = new RunningJobsSeries(runningJobsData);
        series.push({
            data: runningJobsData,
            color: runningJobsSeries.color,
            name: runningJobsSeries.name
        });

        let chartProps: ChartProps = {
            chartType: Charts_Contracts.ChartTypesConstants.Area,
            hostOptions: { height: 200 } as Charts_Contracts.ChartHostOptions,
            legend: { enabled: true, rightAlign: true },
            pageContext: this.context.pageContext,
            series: series,
            specializedOptions: {
                disableStacking: true,
                gradientFill: {
                    linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
                    stops: [
                        {
                            position: 0,
                            color: Colors.PoolGraphGradientColorPalette,
                            opacity: 0
                        },
                        {
                            position: 1,
                            color: Colors.PoolGraphGradientColorPalette,
                            opacity: 0
                        }
                    ]
                },
                includeMarkers: true,
                lineWidth: 2
            },
            suppressAnimation: true,
            title: this._getChartTitle(),
            tooltip: {
                customTooltipMapping: (points: DataPoint[]): TooltipLineItem[] => {
                    return points.map((point, index) => {
                        const seriesValue = Number(point.values[0]).toLocaleString(navigator.language);
                        let tooltipName = `${seriesValue} ${point.seriesName}`;
                        
                        // if last item in tooltip (running jobs) and last point in chart line
                        if (index === points.length - 1 && point.seriesDataIndex === runningJobsData.length - 1) {
                            tooltipName = `${tooltipName} ${Resources.StillAggregating}`;
                        }

                        return {
                            styleType: {
                                color: point.color,
                                type: Charts_Contracts.TooltipLineItemMarkerType.Square
                            },
                            text: tooltipName
                        } as Charts_Contracts.TooltipLineItem;
                    });
                }
            },
            xAxis: {
                allowDecimals: false,
                formatAccessibleLabelsUsingAxisCategories: true,
                labelFormatMode:
                    this.props.confValues.trendBy === TrendBy.Hours
                        ? Charts_Contracts.LabelFormatModes.Textual
                        : Charts_Contracts.LabelFormatModes.DateTime_DayInMonth,
                labelsEnabled: true,
                labelValues: xAxisLabelValues
            },
            yAxis: {
                gridLineOptions: {
                    enabled: true,
                    gridLineColor: gridLineColor
                },
                labelsEnabled: true,
                markingsEnabled: false,
                title: localeFormat(Resources.PoolTrendYAxisLabel.toLocaleUpperCase())
            }
        };

        return chartProps;
    };

    private _verifyChartData = (chartData: ITrendChartData | undefined): boolean => {
        return (
            !!chartData &&
            chartData.primaryChartData[PoolDataStrings.RunningJobs] &&
            chartData.primaryChartData[PoolDataStrings.RunningJobs].length > 0 &&
            chartData.primaryChartData[PoolDataStrings.Concurrency] &&
            chartData.primaryChartData[PoolDataStrings.Concurrency].length > 0
        );
    };

    private _onStoreUpdate = () => {
        const { isPublic, store } = this.props;
        this.setState(isPublic ? store.getPublicTrendState() : store.getPrivateTrendState());
    };

    private _getChartTitle = () => {
        const { isHosted, isPublic } = this.props;
        return !isHosted
            ? isPublic
                ? Resources.AgentUsage
                : Resources.PrivateSelfHostedConcurrency
            : isPublic
            ? Resources.PublicHostedConcurrency
            : Resources.PrivateHostedConcurrency;
    };

    private performOnDataRendered() {
        if (this.state.data || this.state.errorText) {
            if (this.props.onDataRendered && !this._onDataRenderedCalled) {
                this.props.onDataRendered();
                this._onDataRenderedCalled = true;
            }
        }
    }
}
